import { useRef, useState, useEffect } from "react";
import { useController, useFormContext } from "react-hook-form";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import {
  FileUploadOutlined as UploadIcon,
  DeleteOutlineOutlined as DeleteIcon,
  InsertDriveFile as FileIcon,
  PreviewOutlined as PreviewIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";

import DSButton from "../../theme/components/Button.jsx";
import DocumentViewer from "../MDDForm/Viewer/DocumentViewer.jsx";
import { componentTokens } from "../../theme/tokens/components.js";
import { colorsFoundation, typographyTokens } from "../../theme/index.js";
import { backendGet } from "../../services/dataService.js";

const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "tif", "tiff"];
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB

/**
 * A file upload component integrated with react-hook-form.
 * Stores file data in the form state for submission with the parent form.
 * Files are uploaded server-side when the parent form is saved.
 *
 * Form value format:
 * - Saved files: { id, filename }
 * - Pending files: { filename, filedata, mimetype, filetype, filesize }
 *
 * @param {string} name - Form field name
 * @param {string} label - Field label
 * @param {boolean} disabled - Whether the field is disabled
 */
const FormFileUpload = ({ name, label, disabled }) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const [reading, setReading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [fileName, setFileName] = useState(value?.filename || "");
  const [documentToView, setDocumentToView] = useState(null);
  const fileInputRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  // Sync fileName state when value changes (e.g., when editing existing data)
  useEffect(() => {
    setFileName(value?.filename || "");
  }, [value]);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate extension
    const extension = file.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      enqueueSnackbar(
        `Ungültige Dateiendung. Erlaubt: ${ALLOWED_EXTENSIONS.join(", ")}`,
        { variant: "error" }
      );
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      enqueueSnackbar("Datei ist zu groß. Maximum: 15 MB", {
        variant: "error",
      });
      return;
    }

    setReading(true);

    try {
      // Read file as base64
      const fileData = await readFileAsBase64(file);

      // Store full file data in form state (will be uploaded with parent form)
      onChange({
        filename: file.name,
        filesize: file.size,
        mimetype: file.type || "application/octet-stream",
        filetype: getFileType(file.name),
        filedata: fileData,
        label: file.name,
      });
      setFileName(file.name);
    } catch (err) {
      console.error("File read failed:", err);
      enqueueSnackbar("Fehler beim Lesen der Datei", { variant: "error" });
    } finally {
      setReading(false);
      // Reset input so same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemove = () => {
    onChange(null);
    setFileName("");
  };

  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const getFileType = (filename) => {
    const ext = filename.toLowerCase().split(".").pop();
    if (ext === "pdf") return "PDF";
    if (["jpg", "jpeg", "png", "tif", "tiff"].includes(ext)) return "Image";
    return "Download";
  };

  // Check if we have a file (either saved with id, or pending with filedata)
  const hasFile = value?.id || value?.filedata;

  const handlePreview = async () => {
    if (!value) {
      return;
    }

    // If we have local filedata (pending upload), use it directly
    if (value.filedata) {
      const document = {
        success: true,
        filename: value.filename,
        filesize: value.filesize,
        filetype: value.filetype,
        mimetype: value.mimetype,
        filedata: value.filedata,
        label: value.label || value.filename,
      };

      setDocumentToView(null);
      setTimeout(() => setDocumentToView(document), 0);
      return;
    }

    // Otherwise fetch from API (saved file)
    const fileId = value?.id;
    if (!fileId) {
      return;
    }

    setPreviewing(true);

    try {
      // Fetch file data via authenticated API
      const result = await backendGet(`data/anlages/${fileId}`, {
        label: "Datei laden",
        setIsLoading: () => {},
        setResult: () => {},
        enqueueSnackbar,
      });

      if (result) {
        // Format document for DocumentViewer
        const document = {
          success: true,
          id: result.id,
          filename: result.filename,
          filesize: result.filesize,
          filetype: result.filetype,
          mimetype: result.mimetype,
          filedata: result.filedata,
          label: result.bezeichnung || result.filename,
        };

        // Trigger re-render workaround (same as DnDFileUpload)
        setDocumentToView(null);
        setTimeout(() => setDocumentToView(document), 0);
      }
    } catch (err) {
      console.error("Preview failed:", err);
      enqueueSnackbar("Fehler beim Laden der Datei", { variant: "error" });
    } finally {
      setPreviewing(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          mb: 1,
          color: error ? "error.main" : "text.secondary",
          fontSize: typographyTokens.size.sm,
        }}
      >
        {label}
      </Typography>

      {hasFile ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            backgroundColor:
              componentTokens.uploadDropzone?.background?.default || "#fafafa",
          }}
        >
          <FileIcon color="action" fontSize="small" />
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {fileName || "Datei hochgeladen"}
          </Typography>
          <IconButton
            size="small"
            onClick={handlePreview}
            disabled={previewing}
            aria-label="Datei anzeigen"
            title="Datei anzeigen"
          >
            {previewing ? (
              <CircularProgress size={16} />
            ) : (
              <PreviewIcon fontSize="small" color="primary" />
            )}
          </IconButton>
          {!disabled && (
            <IconButton
              size="small"
              onClick={handleRemove}
              aria-label="Datei entfernen"
            >
              <DeleteIcon
                fontSize="small"
                sx={{ color: colorsFoundation.status.negative[600] }}
              />
            </IconButton>
          )}
        </Box>
      ) : (
        <Box>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
            onChange={handleFileSelect}
            disabled={disabled || reading}
          />
          <DSButton
            size="small"
            startIcon={
              reading ? <CircularProgress size={16} /> : <UploadIcon />
            }
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || reading}
          >
            {reading ? "Laden..." : "Datei auswählen"}
          </DSButton>
        </Box>
      )}

      {error && <FormHelperText error>{error.message}</FormHelperText>}

      <DocumentViewer key={documentToView?.id} document={documentToView} />
    </Box>
  );
};

FormFileUpload.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};

export default FormFileUpload;
